import { useIsActiveSceneFlatImageTracked } from '@/utils';
import { ThreeElements, useThree } from '@react-three/fiber';
import { useLayoutEffect, useEffect, useRef, useState, RefObject, ReactNode, Ref, forwardRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Camera, Event as ThreeEvent, Group, Object3D, Euler, Quaternion } from 'three';
import { IDesignerState } from '../../../../../typings';
import { ITuple3, IVector3 } from '../../r3f-components/component-data-structure';
import { useBillboardObject3d } from '../../r3f-components/components/CustomBillboard/hooks';
import { TransformControls as TransformControlsImpl } from './TransformControlsThree';

const pick = (haystack: Partial<TransformControlsProps>, keyNames: (keyof Partial<TransformControlsProps>)[]) => {
	// @ts-expect-error needs refactor //TODO: fix this. at the moment it just returns the whole haystack
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	return ({ keyNames } = haystack);
};

const omit = (obj: Partial<TransformControlsProps>, props: (keyof Partial<TransformControlsProps>)[]) => {
	obj = { ...obj };
	props.forEach((prop) => delete obj[prop]);
	return obj;
};

type ControlsProto = {
	enabled: boolean;
};

export type TransformControlsProps = Omit<ThreeElements['group'], 'ref' | 'rotation'> & {
	object?: Object3D | RefObject<Object3D>;
	locked?: boolean;
	startPosition?: number[];
	initialEntityRotation: IVector3;
	entityId?: string;
	enabled?: boolean;
	axis?: string | null;
	mode?: string;
	translationSnap?: number | null;
	rotationSnap?: number | null;
	scaleSnap?: number | null;
	space?: string;
	size?: number;
	showX?: boolean;
	showY?: boolean;
	showZ?: boolean;
	camera?: Camera;
	onChange?: (e?: ThreeEvent) => void;
	onMouseDown?: (e?: ThreeEvent) => void;
	onMouseUp?: (e?: ThreeEvent) => void;
	onObjectChange?: (e?: ThreeEvent, adjustedRotation?: Euler) => void;
	children?: ReactNode;
	ref?: Ref<TransformControlsImpl>;
    billboard?: [boolean, boolean]; // [cylindrical, spherical]
};


const TransformControlsReactWrapper = forwardRef<TransformControlsImpl, TransformControlsProps>(
	({ startPosition, initialEntityRotation, locked, entityId, billboard, onChange, onMouseDown, onMouseUp, onObjectChange, object, ...props }, ref) => {
		const transformOnlyPropNames: (keyof Partial<TransformControlsProps>)[] = ['enabled', 'axis', 'mode', 'translationSnap', 'rotationSnap', 'scaleSnap', 'space', 'size', 'showX', 'showY', 'showZ'];

		const { camera, ...rest } = props;
		const transformProps = pick(rest, transformOnlyPropNames);
		const objectProps = omit(rest, transformOnlyPropNames);
		// @ts-expect-error new in @react-three/fiber@7.0.5
		const defaultControls = useThree((state) => state.controls) as ControlsProto;
		const gl = useThree(({ gl }) => gl);
		const defaultCamera = useThree(({ camera }) => camera);
		const invalidate = useThree(({ invalidate }) => invalidate);
		const explCamera = camera || defaultCamera;
		const [controls] = useState(() => new TransformControlsImpl(explCamera, gl.domElement, { initialEntityRotation, locked }));
		const groupRef = useRef<Group>(null);
		const isFlatOrientation = useIsActiveSceneFlatImageTracked();
        const mode = useSelector((state: IDesignerState) => state.userReducer.transformControlsMode);

        const billboardQuatRef = useRef(new Quaternion());
        const invBillboardQuatRef = useRef(new Quaternion());
        const correctedEulerRef = useRef(new Euler());
        const lockY = !billboard?.[1] ? true : false;

        useBillboardObject3d({object3d: groupRef, rotation: initialEntityRotation as ITuple3, follow: !!billboard?.[0], lockY, billboardQuatRef});

		const onObjectRotationChange = useCallback(
			(e: ThreeEvent<"objectChange", TransformControlsImpl<Camera>>) => {
				if (billboard?.[0]) {
                    if (!groupRef.current) return;
					invBillboardQuatRef.current.copy(billboardQuatRef.current).invert();
					const correctedQuat = groupRef.current.quaternion.clone().premultiply(invBillboardQuatRef.current);
                    onObjectChange?.(e, correctedEulerRef.current.setFromQuaternion(correctedQuat));
				} else {
					onObjectChange?.(e);
				}
			},
			[billboard, groupRef, invBillboardQuatRef, billboardQuatRef, correctedEulerRef, onObjectChange, lockY]
		);

		useEffect(() => {
			controls.locked = !!locked;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [locked]);
    
		// Without this, manipulating the position in transform panel will move the entity but not the gizmo
		useLayoutEffect(() => {
			if (!object && startPosition) groupRef.current?.position.set(startPosition[0], startPosition[1], startPosition[2]);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [startPosition]);

		useLayoutEffect(() => {
			groupRef.current?.rotation.set(initialEntityRotation[0], initialEntityRotation[1], initialEntityRotation[2]);
		}, [initialEntityRotation]);

		useLayoutEffect(() => {
			controls?.attach(groupRef.current as Group);
			if (!object && startPosition) {
				groupRef.current?.position.set(startPosition[0], startPosition[1], startPosition[2]);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [entityId]);

		useEffect(() => {
			if (defaultControls) {
				const callback = (event: { value: boolean } & ThreeEvent<"dragging-changed", TransformControlsImpl<Camera>>) => {
                    defaultControls.enabled = !event.value
                };
				controls.addEventListener('dragging-changed', callback);
				return () => controls.removeEventListener('dragging-changed', callback);
			}
		}, [controls, defaultControls]);

		useEffect(() => {
			const callback = (e: ThreeEvent) => {
				invalidate();
				if (onChange) onChange(e);
			};

			controls?.addEventListener?.('change', callback);
			if (onMouseDown) controls?.addEventListener?.('mouseDown', onMouseDown);
			if (onMouseUp) controls?.addEventListener?.('mouseUp', onMouseUp);
			if (onObjectChange) {
				if (mode !== 'rotate') controls?.addEventListener?.('objectChange', onObjectChange);
                else controls?.addEventListener?.('objectChange', onObjectRotationChange);
			}

			return () => {
				controls?.removeEventListener?.('change', callback);
				if (onMouseDown) controls?.removeEventListener?.('mouseDown', onMouseDown);
				if (onMouseUp) controls?.removeEventListener?.('mouseUp', onMouseUp);
				if (onObjectChange) controls?.removeEventListener?.('objectChange', onObjectChange);
                if (onObjectChange) controls?.removeEventListener?.('objectChange', onObjectRotationChange);
			};
		}, [onChange, onMouseDown, onMouseUp, onObjectChange, controls, invalidate, mode, onObjectRotationChange]);

		// This is required in image tracked flat scenes to counteract the behaviour of the rotation of the "Editor Canvas Group" named parent component ( within EditorCanvas.tsx )
		useLayoutEffect(() => {
			if (isFlatOrientation) {
				controls.rotation.x = Math.PI / 2;
			} else {
				controls.rotation.x = 0;
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isFlatOrientation]);

		return controls ? (
			<group>
                <primitive ref={ref} dispose={undefined} object={controls} {...transformProps} />
                <group ref={groupRef} >
                    <group {...objectProps} />
                </group>
            </group>
		) : null;
	}
);

TransformControlsReactWrapper.displayName = 'TransformControlsReactWrapper';
export default TransformControlsReactWrapper;
